// api.ts
import axios from "axios";

let authToken: string | null = null;

// Setter function so you can update the token after login
export const setAuthToken = (token: string) => {
  authToken = token;
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "/api",
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    if (config.method?.toUpperCase() !== "OPTIONS" && authToken) {
      config.headers["Authorization"] = `Bearer ${authToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      console.error("Unauthorized: Token invalid or missing");
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const getMinisters = () => api.get("/aproposministere");
export const getNews = () => api.get("/actualites");
export const deleteNews = (id: number) => api.delete(`/actualites/${id}`);
export const getNewsCategorie = () => api.get("/actualitescategories");
// api.ts

export const createMinister = (data: {
  id?: number;
  titrearabe: string;
  titrefrancais: string;
  textarabe: string;
  textfrancais: string;
  image: string | null; // base64 image or null
}) => {
  return api.post("/aproposministere", data);
};

export const updateMinister = (
  id: number,
  data: {
    titrearabe: string;
    titrefrancais: string;
    textarabe: string;
    textfrancais: string;
    image: string | null;
  }
) => {
  return api.put(`/aproposministere/${id}`, data);
};
export const deleteMinister = (id: number) =>
  api.delete(`/aproposministere/${id}`);
export const createNews = (data: {
  id?: number;
  titrearabe: string;
  titrefrancais: string;
  textarabe: string;
  textfrancais: string;
  image: string | null; // base64 image or null
  lienar?: string;
  lienfr?: string;
}) => {
  return api.post("/actualites", data);
};
// Actualites Categories
export const getActualitesCategories = () => api.get("/actualitescategories");
export const createActualiteCategorie = (data: {
  nomarabe: string;
  nomfrancais: string;
}) => api.post("/actualitescategories", data);
export const updateActualiteCategorie = (
  id: number,
  data: { nomarabe: string; nomfrancais: string }
) => api.put(`/actualitescategories/${id}`, data);
export const deleteActualiteCategorie = (id: number) =>
  api.delete(`/actualitescategories/${id}`);

export const updateNews = (
  id: number,
  data: {
    titrearabe: string;
    titrefrancais: string;
    textarabe: string;
    textfrancais: string;
    image: string | null;
    lienar?: string;
    lienfr?: string;
  }
) => {
  return api.put(`/actualites/${id}`, data);
};

// Add these to your existing api.ts file

// Announcements API
export const getAnnouncements = () => api.get("/annonces");
export const getAnnouncementById = (id: number) => api.get(`/annonces/${id}`);

export const createAnnouncement = (data: {
  titrearabe: string;
  titrefrancais: string;
  textarabe: string;
  textfrancais: string;
  datepublication: string;
}) => api.post("/annonces", data);
export const updateAnnouncement = (
  id: number,
  data: {
    titrearabe: string;
    titrefrancais: string;
    textarabe: string;
    textfrancais: string;
    datepublication: string;
  }
) => api.put(`/annonces/${id}`, data);
export const deleteAnnouncement = (id: number) => api.delete(`/annonces/${id}`);
// Information API
export const getInformation = () => api.get("/informations");
export const updateInformation = (id: number, data: any) =>
  api.put(`/informations/${id}`, data);
export const createInformation = (data: any) => api.post("/informations", data);

// Messages API
export const getMessages = () => api.get("/messages");
export const getMessage = (id: number) => api.get(`/messages/${id}`);
export const updateMessage = (id: number, data: any) =>
  api.put(`/messages/${id}`, data);
export const deleteMessage = (id: number) => api.delete(`/messages/${id}`);
export const createMessage = (data: any) => api.post("/messages", data);

// Legislation API
export const getLegislations = () => api.get("/lois");
export const getLegislationsRapide = () => api.get("/lois/rapide");
export const getLegislationFile = (
  id: number,
  fileType: "arabe" | "francais"
) => api.get(`/lois/fichier${fileType}/${id}`);

export const createLegislation = (data: {
  titrearabe: string;
  titrefrancais: string;
  datesortie: string;
  fichierarabe: string | null;
  fichierfrancais: string | null;
}) => api.post("/lois", data);

export const updateLegislation = (
  id: number,
  data: {
    titrearabe: string;
    titrefrancais: string;
    datesortie: string;
    fichierarabe: string | null;
    fichierfrancais: string | null;
  }
) => api.put(`/lois/${id}`, data);

export const deleteLegislation = (id: number) => api.delete(`/lois/${id}`);
// Slider API
// Slider API
// Fix getSliderById to accept parameter
export const getSliderById = (id: number) => api.get(`/slides/${id}`);

// Add getSliders function
export const getSliders = () => api.get("/slides");
export const createSlider = (data: {
  titrearabe: string;
  titrefrancais: string;
  textarabe: string;
  textfrancais: string;
  image: string | null; // base64
  etat: boolean; // Changed from actif to etat
  ordre?: number; // Added ordre field
  buttons?: {
    titrearabe: string;
    titrefrancais: string;
    lien: string;
  }[];
}) => api.post("/slides", data);

export const updateSlider = (
  id: number,
  data: {
    titrearabe: string;
    titrefrancais: string;
    textarabe: string;
    textfrancais: string;
    image: string | null; // base64
    etat: boolean; // Changed from actif to etat
    ordre?: number; // Added ordre field
    buttons?: {
      titrearabe: string;
      titrefrancais: string;
      lien: string;
    }[];
  }
) => api.put(`/slides/${id}`, data);
export const deleteSlider = (id: number) => api.delete(`/slides/${id}`);
// Bibliothèque Categories API
export const getBibliothequeCategories = () =>
  api.get("/bibliothequecategorie");
export const createBibliothequeCategorie = (data: {
  nomarabe: string;
  nomfrancais: string;
}) => api.post("/bibliothequecategorie", data);
export const updateBibliothequeCategorie = (
  id: number,
  data: { nomarabe: string; nomfrancais: string }
) => api.put(`/bibliothequecategorie/${id}`, data);
export const deleteBibliothequeCategorie = (id: number) =>
  api.delete(`/bibliothequecategorie/${id}`);

// Bibliothèque Items API
export const getBibliothequeItems = () => api.get("/bibliotheque");
export const getBibliothequeByCategory = (categorieId: number) =>
  api.get(`/menu/categorie/${categorieId}`);
export const createBibliothequeItem = (data: {
  descriptionarabe: string;
  descriptionfrancais: string;
  fichiers: string[]; // Base64
  liens?: string[]; // Add optional links array
  categorieId: number;
  ordre: number;
  etat: boolean;
}) => api.post("/bibliotheque", data);

export const updateBibliothequeItem = (
  id: number,
  data: {
    descriptionarabe: string;
    descriptionfrancais: string;
    fichiers: string[];
    liens?: string[]; // Add optional links array
    categorieId: number;
    ordre: number;
    etat: boolean;
  }
) => api.put(`/bibliotheque/${id}`, data);
export const deleteBibliothequeItem = (id: number) =>
  api.delete(`/bibliotheque/${id}`);

// Add to your existing api.ts file

// Users API
export const getUsers = () => api.get("/users");
export const getUserById = (id: number) => api.get(`/users/${id}`);
export const createUser = (data: {
  username: string;
  password: string;
  roleNames: string[];
}) => api.post("/users", data);
export const updateUser = (
  id: number,
  data: {
    username: string;
    password?: string;
    roleNames: string[];
  }
) => api.put(`/users/${id}`, data);
export const deleteUser = (id: number) => api.delete(`/users/${id}`);

// Roles API
export const getRoles = () => api.get("/roles");
export const createRole = (data: { name: string }) => api.post("/roles", data);
export const updateRole = (id: number, data: { name: string }) =>
  api.put(`/roles/${id}`, data);
export const deleteRole = (id: number) => api.delete(`/roles/${id}`);

// Permissions API
export const getPermissions = () => api.get("/permissions");
export const createPermission = (data: {
  name: string;
  roles: string;
  urlPattern: string;
}) => api.post("/permissions", data);
export const updatePermission = (
  id: number,
  data: {
    name: string;
    roles: string;
    urlPattern: string;
  }
) => api.put(`/permissions/${id}`, data);
export const deletePermission = (id: number) =>
  api.delete(`/permissions/${id}`);

// Sous-établissements API
export const getSousEtablissements = () => api.get("/sousetablissements");
export const getSousEtablissementById = (id: number) =>
  api.get(`/sousetablissements/${id}`);
export const createSousEtablissement = (data: {
  titrearabe: string;
  titrefrancais: string;
  descarabe: string;
  descfrancais: string;
  lien: string;
  logo: string | null;
}) => api.post("/sousetablissements", data);
export const updateSousEtablissement = (
  id: number,
  data: {
    titrearabe: string;
    titrefrancais: string;
    descarabe: string;
    descfrancais: string;
    lien: string;
    logo: string | null;
  }
) => api.put(`/sousetablissements/${id}`, data);
export const deleteSousEtablissement = (id: number) =>
  api.delete(`/sousetablissements/${id}`);

// Cabinet Ministre API
// Cabinet Ministre API
export const getCabinetMinistre = () => api.get("/cabinetministre");
export const getCabinetMinistreById = (id: number) =>
  api.get(`/cabinetministre/${id}`);
export const createCabinetMinistre = (data: {
  titrearabe: string;
  titrefrancais: string;
  descarabe: string;
  descfrancais: string;
  fichier?: string | null; // Change from string | undefined to string | null
}) => api.post("/cabinetministre", data);
export const updateCabinetMinistre = (
  id: number,
  data: {
    titrearabe: string;
    titrefrancais: string;
    descarabe: string;
    descfrancais: string;
    fichier?: string | null; // Change from string | undefined to string | null
  }
) => api.put(`/cabinetministre/${id}`, data);
export const deleteCabinetMinistre = (id: number) =>
  api.delete(`/cabinetministre/${id}`);

// Add to api.ts after other API endpoints

// Structure Administrative API
export const getStructureAdministrative = () =>
  api.get("/structureadministrative");
export const getStructureAdminById = (id: number) =>
  api.get(`/structureadministrative/${id}`);
export const createStructureAdmin = (data: {
  titrearabe: string;
  titrefrancais: string;
  textarabe: string;
  textfrancais: string;
  fichierarabe: string | null;
  fichierfrancais: string | null;
}) => api.post("/structureadministrative", data);
export const updateStructureAdmin = (
  id: number,
  data: {
    titrearabe: string;
    titrefrancais: string;
    textarabe: string;
    textfrancais: string;
    fichierarabe: string | null;
    fichierfrancais: string | null;
  }
) => api.put(`/structureadministrative/${id}`, data);
export const deleteStructureAdmin = (id: number) =>
  api.delete(`/structureadministrative/${id}`);

// Mot Ministre API
export const getMotMinistre = () => api.get("/motministre");
export const getMotMinistreById = (id: number) => api.get(`/motministre/${id}`);
export const createMotMinistre = (data: {
  titrearabe: string;
  titrefrancais: string;
  textarabe: string;
  textfrancais: string;
}) => api.post("/motministre", data);
export const updateMotMinistre = (
  id: number,
  data: {
    titrearabe: string;
    titrefrancais: string;
    textarabe: string;
    textfrancais: string;
  }
) => api.put(`/motministre/${id}`, data);
export const deleteMotMinistre = (id: number) =>
  api.delete(`/motministre/${id}`);

// Projets API
export const getProjets = () => api.get("/projets");
export const getProjetById = (id: number) => api.get(`/projets/${id}`);
export const createProjet = (data: {
  titrearabe: string;
  titrefrancais: string;
  descarabe: string;
  descfrancais: string;
  lien: string;
  images: { image: string }[]; // Array of base64 images
}) => api.post("/projets", data);
export const updateProjet = (
  id: number,
  data: {
    titrearabe: string;
    titrefrancais: string;
    descarabe: string;
    descfrancais: string;
    lien: string;
    images: { image: string }[];
  }
) => api.put(`/projets/${id}`, data);
export const deleteProjet = (id: number) => api.delete(`/projets/${id}`);

// Procedures-Services API
export const getProceduresServices = () => api.get("/procedures-services");
export const getProcedureServiceById = (id: number) =>
  api.get(`/procedures-services/${id}`);
export const createProcedureService = (data: {
  titrearabe: string;
  titrefrancais: string;
  textarabe: string;
  textfrancais: string;
  image?: string | null; // base64
  fichier?: string | null; // base64 file
}) => api.post("/procedures-services", data);
export const updateProcedureService = (
  id: number,
  data: {
    titrearabe: string;
    titrefrancais: string;
    textarabe: string;
    textfrancais: string;
    image?: string | null;
    fichier?: string | null;
  }
) => api.put(`/procedures-services/${id}`, data);
export const deleteProcedureService = (id: number) =>
  api.delete(`/procedures-services/${id}`);
export default api;
